import { take } from 'rxjs';
import { inject, Injectable, InjectionToken } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { UserType } from '../../types';
import { GlobalDataService } from '../global-data/global-data.service';
import { SnackbarService } from '../snackbar/snackbar.service';

export interface AdvisorDraftSnapshot {
  kind: 'sendout';
  fields: Record<string, unknown>;
  sendoutId: number | null;
  templateId: number;
  eventId: string | null;
}
export interface AdvisorRecoveryDraft extends AdvisorDraftSnapshot {
  storeId: number;
  route: string;
  expiresAt: number;
}
interface RecoveryRecord {
  version: 1;
  storeId: number;
  route: string;
  expiresAt: number;
  completed: boolean;
  expectedUserId?: number;
  expectedSessionVersion?: string;
  draft: AdvisorRecoveryDraft | null;
}
export const ADVISOR_RECOVERY_KEY = 'sam.advisor-recovery.v1';
export const ADVISOR_RECOVERY_ATTEMPT_KEY = 'sam.advisor-recovery-attempt.v1';
export const ADVISOR_LOGIN_INTENT_KEY = 'sam.advisor-login-intent.v1';
export const ADVISOR_RECOVERY_STORAGE = new InjectionToken<Storage>('Advisor recovery tab storage', {
  providedIn: 'root', factory: () => sessionStorage,
});
export const ADVISOR_NAVIGATION = new InjectionToken<(url: string) => void>('Advisor external navigation', {
  providedIn: 'root', factory: () => url => window.location.assign(url),
});
const DRAFT_FIELDS = ['sendoutDate', 'sendoutName', 'contactPerson', 'contentSubjectLine', 'contentPreheader',
  'contentHeadline', 'contentBodyCopy', 'contentCtaText', 'contentCtaUrl', 'contentImageUrl', 'contentDisclaimer', 'eventName'];
const RECOVERY_TTL = 30 * 60 * 1000;
const LOOP_WINDOW = 2 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class AdvisorSessionRecoveryService {
  private readonly storage = inject(ADVISOR_RECOVERY_STORAGE);
  private readonly redirect = inject(ADVISOR_NAVIGATION);
  private readonly router = inject(Router);
  private readonly global = inject(GlobalDataService);
  private readonly snackbar = inject(SnackbarService);
  private capture?: () => AdvisorDraftSnapshot | null;
  private redirecting = false;
  private expiryNotice?: { token: string; release: () => void };

  constructor() {
    this.global.getSessionData().subscribe(session => {
      if (this.expiryNotice && session.sessionVersion !== this.expiryNotice.token) this.dismissExpiryNotice();
    });
  }

  get hasPendingRecovery(): boolean { return !!this.readRecord(); }

  registerDraftCapture(capture: () => AdvisorDraftSnapshot | null): () => void {
    this.capture = capture;
    return () => { if (this.capture === capture) this.capture = undefined; };
  }

  /** Only the interceptor's matching, server-confirmed expired advisor request may initiate this. */
  beginRecovery(failedSessionVersion: string): boolean {
    const session = this.global.getSessionData().value;
    if (this.redirecting || !session.initialized || session.userType !== UserType.AgillicAdvisor ||
        session.sessionVersion !== failedSessionVersion) return false;
    // Older stored advisor sessions only have a store after a successful store lookup.
    const matchedStoreId = session.matchedStoreId === undefined ? session.store?.id : session.matchedStoreId;
    if (!matchedStoreId || matchedStoreId !== session.store?.id) {
      if (!this.expiryNotice) {
        const action = this.snackbar.holdAction();
        this.expiryNotice = { token: failedSessionVersion, release: action.release };
        action.open(
          'Sessionen er udløbet. Kopiér dine ændringer, før du logger ind igen.',
          'Log ind igen'
        ).onAction().pipe(take(1)).subscribe(() => {
          this.dismissExpiryNotice();
          this.retryLogin();
        });
      }
      return false;
    }
    const now = Date.now();
    const storeId = matchedStoreId;
    const previous = this.readRecord();
    let record: RecoveryRecord;
    try {
      const snapshot = this.capture?.() ?? null;
      let route = this.safeReturnUrl(this.router.url);
      if (snapshot?.sendoutId === null && route.split('?')[0] === '/sendout/new') {
        const query = new URLSearchParams({ templateId: String(snapshot.templateId) });
        if (snapshot.eventId) query.set('eventId', snapshot.eventId);
        const date = this.dateValue(snapshot.fields['sendoutDate']);
        if (date) query.set('sendoutDate', date);
        route = `/sendout/new?${query}`;
      }
      const expiresAt = !snapshot && previous?.storeId === storeId && previous.route === route ? previous.expiresAt : now + RECOVERY_TTL;
      const draft = snapshot ? {
        kind: 'sendout' as const, sendoutId: snapshot.sendoutId, templateId: snapshot.templateId,
        eventId: snapshot.eventId, fields: this.sanitizeFields(snapshot.fields),
        storeId, route, expiresAt,
      } : previous?.storeId === storeId && previous.route === route ? previous.draft : null;
      record = { version: 1, storeId, route, expiresAt, completed: false,
        expectedUserId: session.userId, expectedSessionVersion: session.sessionVersion, draft };
      const encoded = JSON.stringify(record);
      if (encoded.length > 1_000_000) throw new Error('Draft too large for recovery');
      // Never leave the editor if its draft cannot be preserved.
      this.storage.setItem(ADVISOR_RECOVERY_KEY, encoded);
    } catch {
      this.snackbar.show('Sessionen er udløbet, men kladden kunne ikke gemmes midlertidigt. Kopiér dine ændringer, før du logger ind igen.', 15000);
      return false;
    }
    const lastAttempt = this.readAttempt();
    this.global.clearSessionData();
    if (lastAttempt?.storeId === storeId && now >= lastAttempt.at && now - lastAttempt.at < LOOP_WINDOW) {
      void this.router.navigateByUrl('/advisor-session-expired', { replaceUrl: true });
      return false;
    }
    this.redirecting = true;
    if (!this.writeAttempt(storeId)) {
      this.redirecting = false;
      void this.router.navigateByUrl('/advisor-session-expired', { replaceUrl: true });
      return false;
    }
    try { this.redirect(this.loginUrl()); return true; }
    catch {
      this.redirecting = false;
      void this.router.navigateByUrl('/advisor-session-expired', { replaceUrl: true });
      return false;
    }
  }

  completeLogin(storeId: number | null): { returnUrl: string; storeChanged: boolean } {
    this.redirecting = false;
    const record = this.readRecord();
    if (!record) return { returnUrl: '/overview', storeChanged: false };
    const session = this.global.getSessionData().value;
    if (!session.initialized || record.storeId !== storeId || session.userType !== UserType.AgillicAdvisor || session.store?.id !== storeId) {
      this.removeRecord();
      return { returnUrl: '/overview', storeChanged: true };
    }
    record.completed = true;
    record.route = this.safeReturnUrl(record.route);
    try { this.storage.setItem(ADVISOR_RECOVERY_KEY, JSON.stringify(record)); }
    catch {
      this.removeRecord();
      this.snackbar.show('Kladden kunne ikke gendannes. Du er logget ind igen.');
    }
    return { returnUrl: record.route, storeChanged: false };
  }

  expectedExchange(): { expectedUserId?: number; expectedSessionVersion?: string } {
    const record = this.readRecord();
    return record?.expectedUserId ? { expectedUserId: record.expectedUserId, expectedSessionVersion: record.expectedSessionVersion } : {};
  }

  markLoginIntent(): void {
    try { this.storage.setItem(ADVISOR_LOGIN_INTENT_KEY, String(Date.now() + 10 * 60 * 1000)); }
    catch { /* Callback will require confirmation when intent storage is unavailable. */ }
  }

  consumeLoginIntent(): boolean {
    try {
      const expiresAt = Number(this.storage.getItem(ADVISOR_LOGIN_INTENT_KEY));
      this.storage.removeItem(ADVISOR_LOGIN_INTENT_KEY);
      return Number.isFinite(expiresAt) && expiresAt > Date.now();
    } catch { return false; }
  }

  takeDraftForCurrentRoute(matches: (draft: AdvisorRecoveryDraft) => boolean = () => true): AdvisorRecoveryDraft | null {
    const record = this.readRecord();
    const session = this.global.getSessionData().value;
    if (!record?.completed || !record.draft) return null;
    if (!session.initialized || session.userType !== UserType.AgillicAdvisor || session.store?.id !== record.storeId) {
      this.removeRecord();
      return null;
    }
    if (this.safeReturnUrl(this.router.url) !== record.route) return null;
    if (!matches(record.draft)) return null;
    this.removeRecord(); // One matching offer, whether accepted or declined by the editor.
    return record.draft;
  }

  /** Explicit user action can retry once the automatic redirect has been stopped. */
  retryLogin(): boolean {
    this.redirecting = true;
    const record = this.readRecord();
    if (record) {
      // User initiated retry is a fresh exchange; keep the draft and route but drop stale binding constraints.
      delete record.expectedUserId;
      delete record.expectedSessionVersion;
      try { this.storage.setItem(ADVISOR_RECOVERY_KEY, JSON.stringify(record)); }
      catch {
        this.redirecting = false;
        this.snackbar.show('Login kunne ikke startes. Browserens midlertidige lager er ikke tilgængeligt.');
        return false;
      }
    }
    if (record && !this.writeAttempt(record.storeId)) {
      this.redirecting = false;
      this.snackbar.show('Login kunne ikke startes. Browserens midlertidige lager er ikke tilgængeligt.');
      return false;
    }
    this.markLoginIntent();
    try { this.redirect(this.loginUrl()); return true; }
    catch {
      try { this.storage.removeItem(ADVISOR_LOGIN_INTENT_KEY); } catch { /* Intent is unusable after a failed redirect. */ }
      this.redirecting = false;
      this.snackbar.show('Login kunne ikke startes. Prøv igen.');
      return false;
    }
  }

  login(): void {
    // Returning via the home page must not silently discard an unoffered recovery draft.
    if (this.readRecord()) { this.retryLogin(); return; }
    this.clear();
    this.markLoginIntent();
    this.redirect(this.loginUrl());
  }

  private dismissExpiryNotice(): void {
    this.expiryNotice?.release();
    this.expiryNotice = undefined;
  }

  clear(): void {
    this.dismissExpiryNotice();
    this.redirecting = false;
    this.capture = undefined;
    this.removeRecord();
    try { this.storage.removeItem(ADVISOR_RECOVERY_ATTEMPT_KEY); } catch { /* No stored draft to restore. */ }
    try { this.storage.removeItem(ADVISOR_LOGIN_INTENT_KEY); } catch { /* No stored intent to clear. */ }
  }

  private loginUrl(): string {
    return `https://www.clubmatas.dk/advisor/faces/public/exo/advisoridentityprovider?cb=${encodeURIComponent(environment.baseUrl + 'storelogin')}`;
  }

  private readRecord(): RecoveryRecord | null {
    try {
      const value = JSON.parse(this.storage.getItem(ADVISOR_RECOVERY_KEY) ?? 'null') as RecoveryRecord | null;
      if (!value || value.version !== 1 || !Number.isSafeInteger(value.storeId) || value.storeId <= 0 ||
          !Number.isFinite(value.expiresAt) || value.expiresAt <= Date.now() || typeof value.route !== 'string' ||
          typeof value.completed !== 'boolean' || value.expectedUserId !== undefined &&
          (!Number.isSafeInteger(value.expectedUserId) || value.expectedUserId <= 0) ||
          value.expectedSessionVersion !== undefined && typeof value.expectedSessionVersion !== 'string') {
        this.removeRecord();
        return null;
      }
      value.route = this.safeReturnUrl(value.route);
      if (value.draft) {
        if (value.draft.kind !== 'sendout' || value.draft.storeId !== value.storeId ||
            value.draft.expiresAt !== value.expiresAt || value.draft.route !== value.route ||
            !Number.isSafeInteger(value.draft.templateId) || value.draft.templateId <= 0 ||
            value.draft.sendoutId !== null && (!Number.isSafeInteger(value.draft.sendoutId) || value.draft.sendoutId <= 0)) {
          value.draft = null;
        } else value.draft.fields = this.sanitizeFields(value.draft.fields);
      }
      return value;
    } catch { this.removeRecord(); return null; }
  }

  private removeRecord(): void {
    try { this.storage.removeItem(ADVISOR_RECOVERY_KEY); } catch { /* Storage may be disabled. */ }
  }
  private readAttempt(): { storeId: number; at: number } | null {
    try {
      const value = JSON.parse(this.storage.getItem(ADVISOR_RECOVERY_ATTEMPT_KEY) ?? 'null');
      return value && Number.isSafeInteger(value.storeId) && Number.isFinite(value.at) ? value : null;
    } catch { return null; }
  }
  private writeAttempt(storeId: number): boolean {
    try {
      this.storage.setItem(ADVISOR_RECOVERY_ATTEMPT_KEY, JSON.stringify({ storeId, at: Date.now() }));
      return true;
    } catch { return false; }
  }
  private sanitizeFields(fields: Record<string, unknown>): Record<string, unknown> {
    const clean: Record<string, unknown> = {};
    for (const key of DRAFT_FIELDS) {
      const value = fields?.[key];
      if (key === 'sendoutDate') { const date = this.dateValue(value); if (date) clean[key] = date; }
      else if (typeof value === 'string' || value === null) clean[key] = value;
    }
    return clean;
  }
  private dateValue(value: unknown): string | null {
    if (value instanceof Date && Number.isFinite(value.getTime())) {
      return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`;
    }
    return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}(?:T.*)?$/.test(value) ? value.slice(0, 10) : null;
  }
  private safeReturnUrl(value: string): string {
    try {
      if (!value.startsWith('/') || value.startsWith('//') || value.includes('\\')) return '/overview';
      const tree = this.router.parseUrl(value);
      const segments = tree.root.children['primary']?.segments ?? [];
      const path = '/' + segments.map(segment => segment.path).join('/');
      if (!/^\/(overview|store-sendouts|sendouts|templates|template\/\d+|sendout\/(\d+(\/select-event)?|new|create\/select-(template|event)))$/.test(path)) return '/overview';
      const query = new URLSearchParams();
      for (const key of ['templateId', 'eventId', 'sendoutDate']) {
        const field = tree.queryParams[key];
        if (typeof field === 'string') query.set(key, field);
      }
      return path + (query.size ? `?${query}` : '');
    } catch { return '/overview'; }
  }
}
